<section class="components-screen-page">
  <ui-page-header [actionsAriaLabel]="'Component screen actions'">
    <ui-button variant="secondary" (click)="showToast('info')">Open Playground Event</ui-button>
  </ui-page-header>

  <p class="intro-copy">
    This is a live playground of all shared UI wrappers. Each panel below renders the real component with test data and interactive
    behavior.
  </p>

  <section class="catalog-grid" aria-label="live wrappers showcase">
    @for (component of components(); track component.id) {
      <ui-card [title]="component.name + ' Showcase'">
        <article class="catalog-item">
          <header class="catalog-item__header">
            <p class="catalog-item__selector">{{ component.selector }}</p>
            <ui-badge [label]="component.usageStatus" [tone]="component.usageStatus === 'High' ? 'active' : 'pending'" />
          </header>

          <section class="catalog-item__demo">
            @switch (component.id) {
              @case ('ui-button') {
                <section class="demo-row">
                  <ui-button variant="primary">Primary</ui-button>
                  <ui-button variant="primary" badgeText="5">Primary + Count</ui-button>
                  <ui-button variant="secondary">Secondary</ui-button>
                  <ui-button variant="danger">Danger</ui-button>
                  <ui-button variant="ghost">Ghost</ui-button>
                </section>
              }
              @case ('ui-table') {
                <section class="demo-stack">
                  <p class="demo-text">
                    Click the first column or any row to open the shared detail drawer. Use the filter action to open the filter drawer.
                  </p>
                  <ui-table
                    [ariaLabel]="'ui-table complete feature demo'"
                    [columns]="tableDemoColumns"
                    [data]="tableDemoRows()"
                    [showToolbar]="true"
                    [showSearch]="true"
                    [showSort]="true"
                    [showFilter]="true"
                    [filterFields]="tableDemoFilterFields"
                    [showPagination]="true"
                    [defaultPageSize]="10"
                    [pageSizeOptions]="[5, 10, 20, 50, 100]"
                    [showPageSizeSelector]="false"
                    [showSelection]="true"
                    [showBulkDeleteButton]="true"
                    [showActionsColumn]="true"
                    [rowClickable]="true"
                    [detailMode]="'side-panel'"
                    [detailTabs]="tableDemoDetailTabs"
                    [badgeRules]="tableDemoBadgeRules"
                    [defaultSortKey]="'component'"
                    [searchKeys]="['component', 'owner', 'category', 'status', 'stock']"
                    (actionClick)="onTableAction($event)"
                  ></ui-table>

                  @if (deleteDialogOpen()) {
                    <ui-dialog
                      [open]="deleteDialogOpen()"
                      [title]="'Delete Component Row?'"
                      [description]="'This action permanently deletes ' + deletingRowName() + ' from the demo table. Do you want to continue?'"
                      [tone]="'danger'"
                      [confirmLabel]="'Yes, Delete'"
                      [cancelLabel]="'No, Keep It'"
                      (cancelAction)="cancelDelete()"
                      (confirmAction)="confirmDelete()"
                    ></ui-dialog>
                  }
                </section>
              }
              @case ('ui-form-field') {
                <section class="demo-stack">
                  <ui-form-field
                    [label]="'Code'"
                    [value]="inputDemoValue()"
                    [hint]="'Signal driven value update'"
                    (valueChange)="inputDemoValue.set($event)"
                  ></ui-form-field>
                  <ui-form-field
                    [label]="'Notes'"
                    [type]="'textarea'"
                    [value]="textareaDemoValue()"
                    [rows]="4"
                    (valueChange)="textareaDemoValue.set($event)"
                  ></ui-form-field>
                </section>
              }
              @case ('ui-select') {
                <section class="demo-stack">
                  <ui-select
                    [label]="'Status'"
                    [value]="selectDemoValue()"
                    [options]="selectOptions"
                    [hint]="'Reusable select options'"
                    (valueChange)="selectDemoValue.set($event)"
                  ></ui-select>
                  <ui-select
                    [label]="'Assigned Agencies'"
                    [multiple]="true"
                    [values]="selectDemoValues()"
                    [options]="selectOptions"
                    [hint]="'Multi-select support for assignment flows'"
                    (valuesChange)="selectDemoValues.set($event)"
                  ></ui-select>
                </section>
              }
              @case ('ui-modal') {
                <ui-modal [title]="'Create Agency'" [description]="'Reusable modal shell with projected content.'">
                  <section class="demo-stack">
                    <ui-form-field [label]="'Agency Name'" [value]="'North Region Hub'"></ui-form-field>
                    <ui-button variant="primary">Save</ui-button>
                  </section>
                </ui-modal>
              }
              @case ('ui-dialog') {
                <section class="demo-stack">
                  <p class="demo-text">
                    Shared confirmation dialog used for destructive actions. Click to open and test Yes/No flow.
                  </p>
                  <section class="demo-row">
                    <ui-button variant="danger" (click)="deleteDialogOpen.set(true)">Open Delete Dialog</ui-button>
                  </section>
                </section>
              }
              @case ('ui-badge') {
                <section class="demo-row">
                  <ui-badge label="Active" tone="active" />
                  <ui-badge label="Trial" tone="trial" />
                  <ui-badge label="Paid" tone="paid" />
                  <ui-badge label="Pending" tone="pending" />
                  <ui-badge label="Overdue" tone="overdue" />
                </section>
              }
              @case ('ui-card') {
                <ui-card title="Nested Card Example">
                  <p class="demo-text">Card wrapper standardizes grouped content surfaces across all features.</p>
                </ui-card>
              }
              @case ('ui-stat-card') {
                <section class="demo-row">
                  <ui-stat-card [label]="'Active Users'" [value]="'1,248'" [delta]="'+8.4%'"></ui-stat-card>
                  <ui-stat-card [label]="'Monthly Sales'" [value]="'$74,330'" [delta]="'+3.1%'"></ui-stat-card>
                </section>
              }
              @case ('ui-sidebar') {
                <section class="sidebar-demo">
                  <ui-sidebar [items]="sidebarDemoItems" [user]="sidebarDemoUser"></ui-sidebar>
                </section>
              }
              @case ('ui-topbar') {
                <ui-topbar [breadcrumbs]="topbarDemoBreadcrumbs" [user]="topbarDemoUser"></ui-topbar>
              }
              @case ('ui-page-header') {
                <ui-page-header [actionsAriaLabel]="'Page header actions'">
                  <ui-button variant="secondary">Export</ui-button>
                  <ui-button variant="primary">Create</ui-button>
                </ui-page-header>
              }
              @case ('ui-page-tabs-header') {
                <ui-page-tabs-header
                  [title]="'Create Supervisor'"
                  [description]="'Sticky page title with optional tabs for long forms and settings pages.'"
                  [tabs]="filterOptions"
                  [activeTab]="selectedFilter()"
                  (tabSelected)="onFilterChange($event)"
                ></ui-page-tabs-header>
              }
              @case ('ui-form-section') {
                <ui-form-section
                  [title]="'Address Information'"
                  [description]="'Reusable section shell with projected content and consistent card spacing.'"
                >
                  <section class="demo-stack">
                    <ui-form-field [label]="'Street Address'" [value]="'12 Boulevard de la Liberte'"></ui-form-field>
                    <ui-form-field [label]="'City'" [value]="'Douala'"></ui-form-field>
                  </section>
                </ui-form-section>
              }
              @case ('ui-form-action-footer') {
                <ui-form-action-footer
                  [showBack]="true"
                  [showSave]="true"
                  [showNext]="true"
                  (backClicked)="showToast('info')"
                  (saveClicked)="showToast('success')"
                  (nextClicked)="showToast('info')"
                ></ui-form-action-footer>
              }
              @case ('ui-chart') {
                <ui-chart [title]="'Monthly Usage Trend'" [series]="chartSeries"></ui-chart>
              }
              @case ('ui-pipeline-strip') {
                <ui-pipeline-strip [stages]="pipelineStages"></ui-pipeline-strip>
              }
              @case ('ui-filter-bar') {
                <ui-filter-bar [filters]="filterOptions" [selected]="selectedFilter()" (changed)="onFilterChange($event)"></ui-filter-bar>
              }
              @case ('ui-toast') {
                <section class="demo-stack">
                  <section class="demo-row">
                    <ui-button variant="secondary" (click)="showToast('info')">Info</ui-button>
                    <ui-button variant="primary" (click)="showToast('success')">Success</ui-button>
                    <ui-button variant="danger" (click)="showToast('error')">Error</ui-button>
                  </section>
                  <ui-toast
                    [type]="toastType()"
                    [title]="toastTitle()"
                    [message]="toastMessage()"
                    [visible]="toastVisible()"
                    (dismiss)="dismissToast()"
                  ></ui-toast>
                </section>
              }
              @case ('ui-toggle') {
                <section class="demo-row">
                  <ui-toggle [checked]="toggleDemoValue()" [ariaLabel]="'Toggle demo'" (toggled)="toggleDemoValue.set($event)"></ui-toggle>
                </section>
              }
              @default {
                <p class="demo-text">Live demo not configured yet for this component.</p>
              }
            }
          </section>

          <section class="catalog-item__meta">
            <p><strong>Functionality:</strong> {{ component.functionality }}</p>
            <p><strong>Design Needs:</strong> {{ component.designNeeds }}</p>
            <p><strong>Interaction Notes:</strong> {{ component.interactionNotes }}</p>
            <p><strong>Usage Guidelines:</strong> {{ component.usageGuidelines }}</p>
          </section>
        </article>
      </ui-card>
    }
  </section>
</section>
